import Image from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard } from "../types";
import { STORY_BADGE_STYLES } from "./category-badge-styles";

const MATERIAL_TYPE_LABELS: Partial<
  Record<ExploreStoryCard["materialType"], string>
> = {
  PROMPT: "Prompt",
  WORKFLOW: "Workflow",
  RELEASE: "Release",
  DEAL: "Deal",
};

interface FeaturedStoryCardProps {
  story: ExploreStoryCard;
  exploreBase: string;
}

/**
 * Horizontal card for the Fresh Stories list.
 * Fixed image dimensions guarantee uniform card height.
 *
 * Spacing hierarchy (inner/outer):
 *   card-to-card  16 px  (parent gap-4)
 *   card padding  14 px  (p-3.5)
 *   img→text gap  14 px  (gap-3.5)
 *   content gap   10 px  (gap-2.5)
 */
export function FeaturedStoryCard({
  story,
  exploreBase,
}: FeaturedStoryCardProps) {
  const primaryTag = story.productTags[0] ?? story.topicTags[0];
  const materialLabel = MATERIAL_TYPE_LABELS[story.materialType];
  const badgeStyleClass = STORY_BADGE_STYLES[story.storyBadge];
  const shouldOptimizeImage = story.coverImageUrl
    ? canOptimizeExploreImage({ src: story.coverImageUrl })
    : false;

  return (
    <Link
      className="group hover:-translate-y-px flex items-center gap-3.5 rounded-2xl border border-[var(--explore-card-border)] bg-[var(--explore-bg-card)] p-2 no-underline shadow-[var(--explore-card-shadow)] transition-all hover:shadow-[var(--explore-card-shadow-hover)] active:scale-[0.995]"
      href={`${exploreBase}/stories/${story.storyId}`}
      prefetch={false}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      {/* Image — generous fixed dimensions */}
      <div className="relative h-[80px] w-[120px] shrink-0 overflow-hidden rounded-l-xl bg-gradient-to-br from-[var(--explore-tag-bg)] to-[var(--explore-border-light)] sm:h-[92px] sm:w-[148px]">
        {story.coverImageUrl && (
          <Image
            alt={story.headline}
            className="h-full w-full object-cover"
            fill
            sizes="(max-width: 640px) 120px, 148px"
            src={story.coverImageUrl}
            unoptimized={!shouldOptimizeImage}
          />
        )}

        {story.isNew && (
          <div className="absolute top-2 left-2 z-10 h-2.5 w-2.5 animate-[explore-pulse-dot_1.5s_ease-in-out_infinite] rounded-full bg-[var(--explore-new-indicator)] shadow-[0_0_0_3px_var(--explore-new-indicator-ring)]" />
        )}
      </div>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col gap-2.5">
        <h3
          className="line-clamp-2 text-pretty font-semibold text-[1.3rem] text-[var(--explore-text-primary)] leading-[1.3] sm:text-[1.35rem]"
          style={{ fontFamily: "var(--explore-card-title-stack)" }}
        >
          {story.headline}
        </h3>

        <div className="flex flex-wrap items-center gap-1.5">
          {materialLabel && (
            <span
              className={`inline-flex items-center rounded-sm px-2 py-0.5 font-bold text-[0.6rem] uppercase tracking-[0.05em] ${badgeStyleClass}`}
            >
              {materialLabel}
            </span>
          )}
          {story.categoryEmoji && (
            <span className="inline-flex items-center justify-center rounded-sm bg-[var(--explore-tag-bg)] px-1.5 py-0.5 text-[0.75rem]">
              {story.categoryEmoji}
            </span>
          )}
          {primaryTag && (
            <span className="inline-flex items-center rounded-sm bg-[var(--explore-tag-bg)] px-2 py-0.5 font-medium text-[0.72rem] text-[var(--explore-tag-text)]">
              {primaryTag.displayName}
            </span>
          )}
          <span className="text-[0.72rem] text-[var(--explore-text-muted)]">
            {story.timeAgo}
          </span>
        </div>
      </div>
    </Link>
  );
}
